import { Chip, Spinner, useDisclosure } from '@ds';
import { LinkToCase } from '@ui';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { DirectAcquisitionBanner } from '@features/components/CaseCommon/DirectAcquisitionBanner';
import {
  RandomizationResults,
  SectionCard,
  SectionRow,
  TabSection,
  TrialQuestionnaireResults,
} from '@features/components/CaseOverview/components';
import { ServiceOutcome } from '@features/components/CaseOverview/components/ServiceOutcome/ServiceOutcome';
import { caseFinishingStatuses, caseServiceTypes } from '@/constants/case/case.constants';
import { isCaseTrial, yesNo } from '@/utils/helpers';
import { CaseDetailsPermissions } from '@/permissions';
import { ServiceRegionId, serviceRegionNameById } from '@/constants/serviceRegion.constants';
import { Case, CaseStatus, DonorType, Laterality, OrganType, ServiceType } from '@/api/apis/case';
import { NrpChips } from '../components/NrpChips';
import { EditGeneralCaseModal } from '../components/EditCaseForms/EditCaseGeneral/EditGeneralCaseModal';
import { NopServiceDetailsRow } from '../components/NopServiceDetailsRow/NopServiceDetailsRow';
import { TxCenterServiceApprovalRow } from '../components/TxCenterServiceApprovalRow';
import { useLogisticsProviders } from '../hooks/useLogisticsProviders';

export const General = ({ caseData }: { caseData: Case }) => {
  const { checkPermission, checkOwnCasePermission } = useContext(AuthContext);
  const hasCaseEditPermission = checkPermission(CaseDetailsPermissions.CaseGeneralUpdate);
  const hasDAEditPermission = checkPermission(CaseDetailsPermissions.CaseDAGeneralUpdate);
  const hasScsMethodEditPermission =
    checkOwnCasePermission(CaseDetailsPermissions.OwnCaseScsMethodUpdate, caseData) ||
    checkPermission(CaseDetailsPermissions.CaseSCSMethodUpdate);

  const {
    caseId,
    donorId,
    organType,
    donorType,
    laterality,
    serviceType,
    serviceDetails,
    opoRequest,
    reAllocationCaseId,
    status,
    outcomeData,
    clinicalData,
    txCenterDetails,
    additionalServices,
    isQuestionaryEditAllowed,
    txCenterServiceApprovalStatus,
    randomization,
    trialProgram,
    questionnaire,
    organApproval,
    serviceRegionId,
    reallocationData,
    // isExpeditedCase,
    logisticsReference,
  } = caseData;

  const isDirectAcquisition = serviceType === ServiceType.DirectAcquisition;
  const hasEditPermission = hasCaseEditPermission || (isDirectAcquisition && hasDAEditPermission);

  const { providers, isLoading: routeLoading } = useLogisticsProviders(logisticsReference?.id);
  const { isOpen: isOpenGeneral, onOpen: onOpenGeneral, onClose: onCloseGeneral } = useDisclosure();
  const isKidneyLateralityEditableAtFinished = organType === OrganType.Kidney && status === CaseStatus.Finished;
  const disableEditing =
    !hasEditPermission || (caseFinishingStatuses.includes(status) && !isKidneyLateralityEditableAtFinished);

  const isTrialCase = isCaseTrial(organType, trialProgram, questionnaire);

  return (
    <TabSection onSectionEdit={disableEditing ? undefined : onOpenGeneral} title="Case General Info">
      {isOpenGeneral && (
        <EditGeneralCaseModal
          isOpen={isOpenGeneral}
          onClose={onCloseGeneral}
          caseData={caseData}
          routeProviders={providers}
        />
      )}
      <SectionCard>
        <SectionRow label="Service Region">
          <Chip size="sm">{serviceRegionNameById[serviceRegionId as ServiceRegionId]}</Chip>
        </SectionRow>
        <SectionRow label="Donor ID">{donorId}</SectionRow>
        <SectionRow label="Organ">{organType}</SectionRow>
        {organType === OrganType.Kidney && (
          <SectionRow label="Laterality">{laterality !== Laterality.NotDefined ? laterality : undefined}</SectionRow>
        )}
        <SectionRow label="Donor Type">{donorType}</SectionRow>
        <SectionRow label="NOP Service">{caseServiceTypes[serviceType]}</SectionRow>
        {serviceType !== ServiceType.DirectAcquisition && <NopServiceDetailsRow serviceDetails={serviceDetails} />}
        {serviceType === ServiceType.DirectAcquisition && <DirectAcquisitionBanner />}
        <SectionRow label="OPO Request">
          <Chip size="sm">{yesNo(opoRequest)}</Chip>
        </SectionRow>
        {/**
         temporarily hidden in scope of - TMDXLP-6792
         <SectionRow label="Expedited Case">
            <Chip size="sm">{yesNo(isExpeditedCase)}</Chip>
         </SectionRow>
         */}
        <SectionRow truncate={false} label="Logistics Provider">
          {routeLoading ? <Spinner size="sm" /> : providers.join(', ') || '-'}
        </SectionRow>
        {donorType === DonorType.DCD && <NrpChips clinicalData={clinicalData} />}
        <SectionRow label="Re-allocation">
          <div className="flex gap-4 items-center">
            <Chip size="sm">{yesNo(!!reAllocationCaseId)}</Chip>
            {reAllocationCaseId && <LinkToCase id={reAllocationCaseId} />}
          </div>
        </SectionRow>
        <SectionRow label="Additional Services">
          <Chip size="sm">{yesNo(additionalServices)}</Chip>
        </SectionRow>
        <TxCenterServiceApprovalRow
          txCenterServiceApprovalStatus={txCenterServiceApprovalStatus}
          reviews={caseData.reviews}
        />
        <ServiceOutcome
          randomization={randomization}
          status={status}
          outcomeData={outcomeData}
          clinicalData={clinicalData}
        />
        {isTrialCase && (
          <>
            <TrialQuestionnaireResults
              hasEditPermission={hasScsMethodEditPermission}
              reallocationData={reallocationData}
              trialProgram={trialProgram}
              questionnaire={questionnaire}
              caseId={caseId}
              caseStatus={status}
            />
            <RandomizationResults
              reallocationData={reallocationData}
              questionnaire={questionnaire}
              organApproval={organApproval}
              trialProgram={trialProgram!}
              txCenterDetails={txCenterDetails}
              randomization={randomization}
            />
          </>
        )}
        {!!trialProgram && (
          <SectionRow label="Questionary Edit Allowed">
            <Chip size="sm">{yesNo(isQuestionaryEditAllowed)}</Chip>
          </SectionRow>
        )}
      </SectionCard>
    </TabSection>
  );
};
